import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface UsageMetrics {
  readonly totalRequests: number;
  readonly completedRequests: number;
  readonly blockedRequests: number;
  readonly totalResponses: number;
  readonly avgLatencyMs: number;
  readonly avgConfidenceScore: number;
  readonly totalTokensInput: number;
  readonly totalTokensOutput: number;
  readonly byRequestType: readonly { readonly requestType: string; readonly count: number }[];
}
interface PromptMetrics {
  readonly distinctPromptsUsed: number;
  readonly requestsWithPrompt: number;
  readonly requestsAdHoc: number;
  readonly topPrompts: readonly { readonly promptId: string; readonly count: number }[];
}
interface AdoptionMetrics {
  readonly activeUsers: number;
  readonly totalRequests: number;
  readonly requestsPerUser: number;
  readonly adoptionTargetPct: number;
  readonly approvalsPending: number;
  readonly flaggedResponses: number;
}

/**
 * M38 — AI Analytics Dashboard (§16/§23). Read-only measurement of AI usage,
 * prompt utilisation and enterprise adoption, computed live from the T508 request
 * and T509 response ledgers. Usage covers request/response volume, completion and
 * block counts, average latency + confidence and token consumption. Prompt metrics
 * separate governed-prompt usage from ad-hoc usage. Adoption tracks active users
 * against the governed adoption target (ADOPTION_TARGET_PCT). All routes require
 * ANALYTICS_VIEW; tenant isolation is enforced server-side (LAW 4).
 */
export function AiAnalyticsDashboard(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [prompts, setPrompts] = useState<PromptMetrics | null>(null);
  const [adoption, setAdoption] = useState<AdoptionMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: UsageMetrics }>(`/v1/ai/analytics/usage`, { token }),
      apiRequest<{ data: PromptMetrics }>(`/v1/ai/analytics/prompts`, { token }),
      apiRequest<{ data: AdoptionMetrics }>(`/v1/ai/analytics/adoption`, { token }),
    ])
      .then(([u, p, a]) => { if (alive) { setUsage(u.data); setPrompts(p.data); setAdoption(a.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="ai-analytics-dashboard">
      <h1>{t('m38.analytics.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !usage || !prompts || !adoption ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="ai-usage-metrics">
            <h2>{t('m38.analytics.usageHeading')}</h2>
            <div className="kpi-grid">
              <div className="kpi-card" data-kpi="total-requests"><span className="kpi-label">{t('m38.analytics.totalRequests')}</span><span className="kpi-value" aria-label="usage-total-requests">{usage.totalRequests}</span></div>
              <div className="kpi-card" data-kpi="completed-requests"><span className="kpi-label">{t('m38.analytics.completed')}</span><span className="kpi-value" aria-label="usage-completed">{usage.completedRequests}</span></div>
              <div className="kpi-card" data-kpi="blocked-requests"><span className="kpi-label">{t('m38.analytics.blocked')}</span><span className="kpi-value" aria-label="usage-blocked">{usage.blockedRequests}</span></div>
              <div className="kpi-card" data-kpi="total-responses"><span className="kpi-label">{t('m38.analytics.responses')}</span><span className="kpi-value" aria-label="usage-responses">{usage.totalResponses}</span></div>
              <div className="kpi-card" data-kpi="avg-latency"><span className="kpi-label">{t('m38.analytics.avgLatency')}</span><span className="kpi-value" aria-label="usage-latency">{usage.avgLatencyMs}</span></div>
              <div className="kpi-card" data-kpi="avg-confidence"><span className="kpi-label">{t('m38.analytics.avgConfidence')}</span><span className="kpi-value" aria-label="usage-confidence">{number(usage.avgConfidenceScore)}</span></div>
              <div className="kpi-card" data-kpi="tokens-input"><span className="kpi-label">{t('m38.analytics.inputTokens')}</span><span className="kpi-value" aria-label="usage-tokens-input">{usage.totalTokensInput}</span></div>
              <div className="kpi-card" data-kpi="tokens-output"><span className="kpi-label">{t('m38.analytics.outputTokens')}</span><span className="kpi-value" aria-label="usage-tokens-output">{usage.totalTokensOutput}</span></div>
            </div>
            <DataTable
              ariaLabel="usage-by-request-type"
              rows={usage.byRequestType}
              rowKey={(r) => r.requestType}
              emptyMessage={t('m38.analytics.noRequests')}
              columns={[
                { key: 'requestType', header: t('m38.analytics.colRequestType'), render: (r) => r.requestType },
                { key: 'count', header: t('m38.analytics.colCount'), render: (r) => r.count },
              ]}
            />
          </section>

          <section aria-label="ai-prompt-metrics">
            <h2>{t('m38.analytics.promptHeading')}</h2>
            <dl>
              <dt>{t('m38.analytics.distinctPrompts')}</dt>
              <dd aria-label="prompt-distinct">{prompts.distinctPromptsUsed}</dd>
              <dt>{t('m38.analytics.requestsWithPrompt')}</dt>
              <dd aria-label="prompt-with">{prompts.requestsWithPrompt}</dd>
              <dt>{t('m38.analytics.adHocRequests')}</dt>
              <dd aria-label="prompt-adhoc">{prompts.requestsAdHoc}</dd>
            </dl>
          </section>

          <section aria-label="ai-adoption-metrics">
            <h2>{t('m38.analytics.adoptionHeading')}</h2>
            <dl>
              <dt>{t('m38.analytics.activeUsers')}</dt>
              <dd aria-label="adoption-users">{adoption.activeUsers}</dd>
              <dt>{t('m38.analytics.requestsPerUser')}</dt>
              <dd aria-label="adoption-per-user">{adoption.requestsPerUser}</dd>
              <dt>{t('m38.analytics.adoptionTarget')}</dt>
              <dd aria-label="adoption-target">{number(adoption.adoptionTargetPct)}%</dd>
              <dt>{t('m38.analytics.approvalsPending')}</dt>
              <dd aria-label="adoption-approvals-pending">{adoption.approvalsPending}</dd>
              <dt>{t('m38.analytics.flaggedResponses')}</dt>
              <dd aria-label="adoption-flagged">{adoption.flaggedResponses}</dd>
            </dl>
          </section>
        </>
      )}
    </main>
  );
}
